import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  BookOpen,
  CheckCircle,
  List,
  ArrowLeft,
  MoveRight,
  MoveLeft,
  Check,
  MessageCircle,
  X,
  Award,
  Download,
  FileText,
  Video,
  Music,
  Newspaper,
  Globe,
  Link,
  MessageSquare,
  Bookmark,
  BookmarkCheck,
  PenTool,
  Brain,
  Printer,
} from "lucide-react";
import ContentChatWidget from "@/components/ai/ContentChatWidget";
import LessonChatButton from "./LessonChatButton";
import VoteWidget from "@/components/ui/VoteWidget";
import "../../styles/lesson-content.css";
import "../../styles/lesson-components.css";
import "../../styles/certificate-responsive.css";
import "../../styles/certificate-container.css";
import { getCourseContent, getCourseById } from "@/admin/utils/courseService";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Progress } from "@/components/ui/progress";
import {
  getUserCourseProgress,
  updateUserProgress,
} from "@/services/progressService";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/useAuth";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  createCertificate,
  getUserCertificate,
} from "@/services/certificateService";
import html2canvas from "html2canvas";
import { QRCodeSVG } from "qrcode.react";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useReaderProgress } from "@/hooks/useReaderProgress";
import { DiagramViewer } from "@/components/diagrams/DiagramViewer";
import { QuizPlayer } from "@/components/quiz/QuizPlayer";
import { saveQuizResult } from "@/services/quizService";
import nftCertificateService from "@/services/nftCertificateService";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";

// Định nghĩa kiểu cho node bài học
interface LessonNode {
  id: string;
  name: string;
  content: string;
  type?: string;
  position?: {
    x: number;
    y: number;
  };
  description?: string;
  resources?: Array<{
    type: string;
    title: string;
    url: string;
    description: string;
    id?: string;
    position?: string;
    showDetails?: boolean;
  }>;
  // thêm các trường khác nếu cần
}

interface LessonContentViewProps {
  lesson: LessonNode | null;
  courseId: string;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  onClose: () => void;
}

const LessonContentView: React.FC<LessonContentViewProps> = ({
  lesson,
  courseId,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  onClose,
}) => {
  const { t } = useTranslation(["courses", "common"]);
  const contentRef = useRef<HTMLDivElement>(null);
  const [courseNodes, setCourseNodes] = useState<LessonNode[]>([]);
  const [nodeProgress, setNodeProgress] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isDesktop = useMediaQuery("(min-width: 769px)");
  const { user } = useAuth();
  const [isCurrentLessonCompleted, setIsCurrentLessonCompleted] =
    useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isGeneratingCertificate, setIsGeneratingCertificate] = useState(false);
  const [showCertificateDialog, setShowCertificateDialog] = useState(false);
  const [recipientName, setRecipientName] = useState("");
  const [certificateCreated, setCertificateCreated] = useState(false);
  const [certificateId, setCertificateId] = useState("");
  const [courseName, setCourseName] = useState("");
  const [courseContentData, setCourseContentData] = useState<any>(null);
  const [showChatDialog, setShowChatDialog] = useState(false);
  const [showCertificateResultDialog, setShowCertificateResultDialog] =
    useState(false);
  const [certificate, setCertificate] = useState<any>(null);
  const [currentLesson, setCurrentLesson] = useState<LessonNode | null>(null);
  const [currentLessonId, setCurrentLessonId] = useState("");
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [currentNodeData, setCurrentNodeData] = useState<LessonNode | null>(
    null,
  );
  const [currentNodeId, setCurrentNodeId] = useState("");
  const [suggestedQuestionsForLesson, setSuggestedQuestionsForLesson] =
    useState<string[]>([]);
  const [bookmarked, setBookmarked] = useState(false);
  const navigate = useNavigate();

  // State cho NFT certificate
  const [isCreatingNFT, setIsCreatingNFT] = useState(false);
  const [nftCertificateCreated, setNFTCertificateCreated] = useState(false);
  const [nftTxHash, setNFTTxHash] = useState("");
  const [showWalletConnectDialog, setShowWalletConnectDialog] = useState(false);
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState("");
  const [certificateType, setCertificateType] = useState<"standard" | "nft">(
    "standard",
  );

  // State cho quiz
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizPassed, setQuizPassed] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);

  // State cho kiểm tra xem người dùng đã có chứng chỉ từ trước
  const [hasCertificate, setHasCertificate] = useState(false);

  // State cho việc kiểm tra certificate
  const [isCheckingCertificate, setIsCheckingCertificate] = useState(false);

  // Thêm state mới để kiểm soát việc hiển thị nút chat
  const [isChatButtonVisible, setIsChatButtonVisible] = useState(true);

  // Lấy diagram resources từ lesson
  const diagramResources =
    lesson?.resources?.filter((r) => r.type === "diagram") || [];

  // Hook theo dõi quá trình đọc
  const { progress: readingProgress, startTracking } =
    useReaderProgress(contentRef);

  // Bắt đầu theo dõi khi component được mount
  useEffect(() => {
    if (contentRef.current) {
      startTracking();
    }
  }, [startTracking]);

  // Fetch course data when component mounts
  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        const courseData = await getCourseById(courseId);
        if (courseData) {
          setCourseName(courseData.name);
        }
      } catch (error) {
        console.error("Error fetching course data:", error);
      }
    };

    fetchCourseData();
  }, [courseId]);

  // Lấy danh sách các bài học của khóa học và tiến độ
  useEffect(() => {
    const fetchCourseContent = async () => {
      try {
        setLoading(true);
        console.log("Fetching course content for:", courseId);
        const content = await getCourseContent(courseId);
        
        if (!content) {
          console.error(`Course content not found for course ${courseId}`);
          setError(t("course_content_not_found", { ns: "errors" }));
          setLoading(false);
          return;
        }

        console.log("Course content:", content);

        // Kiểm tra thêm cấu trúc cũ - content là mảng nodes trực tiếp
        if (content && Array.isArray(content)) {
          console.log("Setting course nodes from content array");
          setCourseNodes(content);
        }
        // Kiểm tra nếu content có nodes trực tiếp
        else if (content && Array.isArray(content.nodes)) {
          console.log("Setting course nodes from content.nodes");
          setCourseNodes(content.nodes);
        }
        // Kiểm tra trường hợp nodes nằm trực tiếp trong đối tượng gốc (thường từ AI generate)
        else if (content.content && typeof content.content === 'object' && Array.isArray(content.content.nodes)) {
          console.log("Setting course nodes from content.content.nodes");
          setCourseNodes(content.content.nodes);
        }
        // Trường hợp đặc biệt: nodes nằm trực tiếp trong content (từ JSON AI generate)
        else if (content && content.id === courseId && Array.isArray(content.nodes)) {
          console.log("Setting course nodes from root nodes array:", content.nodes.length);
          setCourseNodes(content.nodes);
        }

        // Load node progress
        loadNodeProgress(courseId);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch course content:", error);
        setLoading(false);
      }
    };

    fetchCourseContent();
  }, [courseId]);

  // Kiểm tra nếu đã hoàn thành quiz trước đó
  useEffect(() => {
    const checkQuizCompletion = async () => {
      if (!lesson?.id) return;

      try {
        // Reset states
        setQuizCompleted(false);
        setQuizPassed(false);
        setShowQuiz(false);

        // Kiểm tra nếu đã hoàn thành quiz trước đó
        const progress = await getUserCourseProgress(courseId);
        if (progress && progress.progress && progress.progress[lesson.id]) {
          // Kiểm tra nếu có quiz_results
          if (progress.quiz_results && progress.quiz_results[lesson.id]) {
            setQuizCompleted(true);
            setQuizPassed(progress.quiz_results[lesson.id].passed);
          }
        }

        // Hiển thị quiz nếu đã cuộn xuống đủ để thấy nội dung
        const quizResource = lesson.resources?.find((r) => r.type === "quiz");
        if (quizResource && readingProgress > 80) {
          setShowQuiz(true);
        }
      } catch (error) {
        console.error("Error checking quiz completion:", error);
      }
    };

    checkQuizCompletion();
  }, [lesson?.id, courseId, readingProgress]);

  // Lưu vị trí cuộn khi rời khỏi trang
  useEffect(() => {
    return () => {
      if (contentRef.current && lesson) {
        localStorage.setItem(
          `lesson_${lesson.id}_scroll`,
          String(contentRef.current.scrollTop),
        );
      }
    };
  }, [lesson]);

  // Khôi phục vị trí cuộn khi quay lại trang
  useEffect(() => {
    if (contentRef.current && lesson) {
      const savedScrollPosition = localStorage.getItem(
        `lesson_${lesson.id}_scroll`,
      );
      if (savedScrollPosition) {
        contentRef.current.scrollTop = parseInt(savedScrollPosition);
      }
    }
  }, [lesson]);

  // Xử lý scroll để theo dõi tiến độ đọc
  useEffect(() => {
    // Tự động cập nhật tiến độ học tập dựa trên vị trí cuộn
    if (readingProgress > 80 && !isCurrentLessonCompleted && lesson) {
      // Đánh dấu hoàn thành tại client side trước
      setIsCurrentLessonCompleted(true);

      // Cập nhật trạng thái hoàn thành
      const handleUpdateProgress = async () => {
        if (!user || !lesson?.id) return;

        try {
          // Cập nhật tiến độ
          await updateUserProgress(courseId, { [lesson.id]: true });

          // Hiển thị toast thông báo
          toast({
            title: "Đã hoàn thành bài học",
            description: "Bạn đã hoàn thành bài học này",
            duration: 3000,
          });
        } catch (error) {
          console.error("Error updating progress:", error);
          // Nếu lỗi, reset trạng thái
          setIsCurrentLessonCompleted(false);
        }
      };

      handleUpdateProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readingProgress > 80, lesson?.id, user, courseId, t]);

  // Thêm useEffect để kiểm tra certificate
  useEffect(() => {
    const checkExistingCertificate = async () => {
      if (!user || !courseId || isCheckingCertificate) return;

      try {
        setIsCheckingCertificate(true);

        const cert = await getUserCertificate(courseId);
        if (cert) {
          setCertificate(cert);
          setHasCertificate(true);

          // Nếu có thông tin NFT, cập nhật state
          if (cert.nft_token_id) {
            setNFTCertificateCreated(true);

            // Nếu có token_id nhưng không có transaction_hash, cập nhật transaction hash
            if (cert.nft_token_id && !cert.nft_transaction_hash) {
              try {
                console.log(
                  "Phát hiện NFT certificate chưa có TX hash, chuẩn bị cập nhật",
                );

                // Gọi hàm cập nhật certificate với transaction hash từ frontend
                await nftCertificateService.updateCertificateWithNFT(
                  cert.certificate_id,
                  cert.nft_token_id,
                  "manual_verified", // Sử dụng giá trị cố định để đánh dấu xác minh thủ công
                  true, // đánh dấu là certificate đã tồn tại
                );

                console.log("Đã cập nhật NFT certificate với TX hash");

                // Cập nhật state với blockchain_verification_url
                if (cert.blockchain_verification_url) {
                  setNFTTxHash(cert.blockchain_verification_url);
                } else {
                  setNFTTxHash("https://giacngo.org/verified");
                }

                // Thông báo cho người dùng (optional)
                toast({
                  title: "Đã cập nhật thông tin NFT",
                  description:
                    "Thông tin chứng chỉ NFT của bạn đã được cập nhật",
                  variant: "default",
                });
              } catch (updateError) {
                console.error(
                  "Lỗi khi cập nhật thông tin NFT TX:",
                  updateError,
                );
              }
            } else {
              // Ưu tiên sử dụng blockchain_verification_url nếu có
              if (cert.blockchain_verification_url) {
                setNFTTxHash(cert.blockchain_verification_url);
              } else if (cert.nft_transaction_hash) {
                setNFTTxHash(cert.nft_transaction_hash);
              } else {
                setNFTTxHash("");
              }
            }
          }
        }
      } catch (error) {
        console.error("Lỗi khi kiểm tra chứng chỉ hiện có:", error);
      } finally {
        setIsCheckingCertificate(false);
      }
    };

    checkExistingCertificate();
  }, [user, courseId]);

  // Thêm effect để kiểm tra trạng thái kết nối ví khi component mount
  useEffect(() => {
    const checkWalletConnection = async () => {
      try {
        const walletStatus =
          await nftCertificateService.checkIfWalletConnected();
        if (walletStatus.connected) {
          setWalletConnected(true);
          setWalletAddress(walletStatus.address);
        }
      } catch (error) {
        console.error("Error checking wallet connection:", error);
      }
    };

    checkWalletConnection();
  }, []);

  // Cập nhật UI cho dialog kết nối ví dựa trên trạng thái kết nối
  useEffect(() => {
    // Đóng dialog kết nối ví nếu đã kết nối
    if (walletConnected && showWalletConnectDialog) {
      setShowWalletConnectDialog(false);
    }
  }, [walletConnected, showWalletConnectDialog]);

  // Thêm useEffect để scroll lên top khi lesson thay đổi
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [lesson?.id]); // Chỉ chạy khi lesson ID thay đổi

  // Các handlers
  const handleChatOpen = () => {
    setIsChatOpen(true);
  };

  const handleChatClose = () => {
    setIsChatOpen(false);
  };

  const handleGetCertificate = () => {
    // Nếu đã có certificate, mở dialog hiển thị certificate
    if (hasCertificate) {
      setShowCertificateResultDialog(true);
      return;
    }

    // Nếu chưa có, hiển thị dialog tạo certificate
    setRecipientName(user?.display_name || user?.full_name || user?.name || "");
    setShowCertificateDialog(true);

    // Thông báo cho người dùng
    toast({
      title: "Bạn sắp tạo chứng chỉ",
      description: "Vui lòng nhập tên của bạn và nhấn nút tạo chứng chỉ",
      duration: 5000,
    });
  };

  const handleGenerateCertificate = async () => {
    if (!recipientName.trim()) {
      toast({
        title: "Tên không hợp lệ",
        description: "Vui lòng nhập tên của bạn",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingCertificate(true);

    try {
      if (!user) {
        throw new Error("Người dùng chưa đăng nhập");
      }

      // Kiểm tra loại chứng chỉ
      if (certificateType === "nft") {
        // Kiểm tra lại một lần nữa trạng thái kết nối ví
        if (!walletConnected) {
          const walletStatus =
            await nftCertificateService.checkIfWalletConnected();

          if (walletStatus.connected) {
            // Cập nhật state nếu ví đã kết nối
            setWalletConnected(true);
            setWalletAddress(walletStatus.address);
          } else {
            // Hiện dialog kết nối ví nếu chưa kết nối
            setShowWalletConnectDialog(true);
            setIsGeneratingCertificate(false);
            return;
          }
        }

        // Đầu tiên, tạo chứng chỉ thông thường trong database
        const certificateResult = await createCertificate(
          courseId,
          recipientName,
          courseNodes.length,
        );

        if (!certificateResult) {
          throw new Error("Không thể tạo chứng chỉ cơ bản");
        }

        setCertificate(certificateResult);

        // Thực hiện tạo NFT certificate
        setIsCreatingNFT(true);

        try {
          // Mint NFT Certificate với metadata
          const mintResult =
            await nftCertificateService.createAndMintNFTCertificate(
              certificateResult,
              walletAddress,
            );

          if (!mintResult.success) {
            throw new Error(
              mintResult.message || "Lỗi khi tạo NFT Certificate",
            );
          }

          // Kiểm tra nếu certificate đã tồn tại
          if (mintResult.alreadyExists) {
            setNFTCertificateCreated(true);

            // Đóng dialog thông tin và mở dialog chứng chỉ
            setShowCertificateDialog(false);
            setShowCertificateResultDialog(true);

            // Hiển thị toast thông báo
            toast({
              title: "Chứng chỉ NFT đã tồn tại!",
              description: "Bạn đã có chứng chỉ NFT cho khóa học này từ trước.",
              variant: "default",
            });

            return;
          }

          // Cập nhật thông tin NFT vào database
          await nftCertificateService.updateCertificateWithNFT(
            certificateResult.certificate_id,
            mintResult.tokenId,
            mintResult.transactionHash,
          );

          // Sau khi tạo NFT thành công
          setNFTCertificateCreated(true);
          setNFTTxHash(mintResult.transactionHash);

          // Đóng dialog thông tin và mở dialog chứng chỉ
          setShowCertificateDialog(false);
          setShowCertificateResultDialog(true);

          // Hiển thị toast thông báo thành công
          toast({
            title: "Đã tạo chứng chỉ NFT thành công!",
            description:
              "Chúc mừng! Bạn đã nhận được chứng chỉ NFT trên Polygon.",
            variant: "default",
          });
        } catch (nftError) {
          console.error("Error minting NFT certificate:", nftError);
          toast({
            title: "Lỗi khi tạo NFT",
            description: `${nftError.message}. Bạn vẫn nhận được chứng chỉ thông thường.`,
            variant: "destructive",
          });

          // Hiển thị chứng chỉ thường dù có lỗi NFT
          setShowCertificateDialog(false);
          setShowCertificateResultDialog(true);
        } finally {
          setIsCreatingNFT(false);
          setIsGeneratingCertificate(false);
        }

        return;
      }

      // Tạo chứng chỉ thông thường trong database
      const result = await createCertificate(
        courseId,
        recipientName,
        courseNodes.length,
      );

      if (result && result.id) {
        setCertificateId(result.id);
        setCertificateCreated(true);
        setCertificate(result);

        // Đóng dialog thông tin và mở dialog chứng chỉ
        setShowCertificateDialog(false);
        setShowCertificateResultDialog(true);

        // Hiển thị toast thông báo thành công
        toast({
          title: "Đã tạo chứng chỉ thành công!",
          description:
            "Chúc mừng! Bạn đã nhận được chứng chỉ hoàn thành khóa học.",
          variant: "default",
        });
      } else {
        // Hiển thị lỗi cụ thể nếu không tạo được chứng chỉ
        toast({
          title: "Không thể tạo chứng chỉ",
          description: "Đã xảy ra lỗi. Hãy thử lại sau hoặc liên hệ hỗ trợ.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error generating certificate:", error);
      toast({
        title: "Lỗi tạo chứng chỉ",
        description: "Đã xảy ra lỗi khi tạo chứng chỉ. Vui lòng thử lại sau.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingCertificate(false);
    }
  };

  // Xử lý tải chứng chỉ
  const handleDownloadCertificate = () => {
    const certificateEl = document.getElementById("certificate-container");
    if (!certificateEl) return;

    // Thông báo đang tải
    toast({
      title: "Đang chuẩn bị chứng chỉ",
      description: "Vui lòng đợi trong giây lát...",
      duration: 3000,
    });

    // Tạo một bản sao của certificate element để xử lý
    const clonedEl = certificateEl.cloneNode(true) as HTMLElement;
    document.body.appendChild(clonedEl);

    // Đặt style tạm thời cho element clone để đảm bảo kích thước chính xác
    Object.assign(clonedEl.style, {
      position: "fixed",
      left: "-9999px",
      top: "-9999px",
      width: "850px",
      height: "600px",
      transform: "none",
      border: "8px double #ca8a04",
      backgroundColor: "#fffef0",
      padding: "20px",
      margin: "0",
      zIndex: "-9999",
      boxSizing: "border-box",
    });

    // Render canvas từ element clone
    html2canvas(clonedEl, {
      scale: 2,
      useCORS: true,
      logging: false,
      width: 850,
      height: 600,
      backgroundColor: "#fffef0",
    })
      .then((canvas) => {
        // Tạo link tải
        const link = document.createElement("a");
        link.download = `chung-chi-giac-ngo-${recipientName.replace(/\s+/g, "-").toLowerCase()}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();

        // Xóa element clone
        document.body.removeChild(clonedEl);

        // Thông báo tải thành công
        toast({
          title: "Tải chứng chỉ thành công",
          description: "Chứng chỉ đã được lưu vào thiết bị của bạn",
          duration: 3000,
        });
      })
      .catch((err) => {
        console.error("Error generating certificate image:", err);
        document.body.removeChild(clonedEl);

        toast({
          title: "Lỗi khi tải chứng chỉ",
          description: "Vui lòng thử lại sau",
          variant: "destructive",
          duration: 3000,
        });
      });
  };

  // Xử lý nút bookmark
  const handleBookmark = () => {
    setBookmarked(!bookmarked);
    toast({
      title: bookmarked
        ? t("common:remove_bookmark")
        : t("common:add_bookmark"),
      description: bookmarked
        ? t("common:remove_bookmark_desc")
        : t("common:add_bookmark_desc"),
      duration: 3000,
    });
  };

  // Helpers
  const getResourceIcon = (type: string) => {
    switch (type) {
      case "article":
        return <Newspaper className="h-5 w-5 text-blue-500" />;
      case "video":
        return <Video className="h-5 w-5 text-red-500" />;
      case "audio":
        return <Music className="h-5 w-5 text-purple-500" />;
      case "document":
        return <FileText className="h-5 w-5 text-green-500" />;
      case "external":
        return <Globe className="h-5 w-5 text-orange-500" />;
      case "diagram":
        return <PenTool className="h-5 w-5 text-indigo-500" />;
      default:
        return <Link className="h-5 w-5 text-gray-500" />;
    }
  };

  // Kiểm tra nếu là nội dung HTML
  const isHtmlContent = (content: string): boolean => {
    return /<\/?[a-z][\s\S]*>/i.test(content);
  };

  // Tiền xử lý HTML content
  const processHtmlContent = (content: string): string => {
    // Thêm các xử lý HTML content nếu cần
    return content;
  };

  // Xử lý markdown đơn giản bằng regex
  const processMarkdownContent = (content: string): string => {
    if (!content) return "";

    let processedContent = content;

    // Xử lý các tiêu đề (headers)
    // H1: # Tiêu đề
    processedContent = processedContent.replace(
      /^# (.+)$/gm,
      '<h1 class="text-3xl font-bold my-4">$1</h1>',
    );
    // H2: ## Tiêu đề
    processedContent = processedContent.replace(
      /^## (.+)$/gm,
      '<h2 class="text-2xl font-bold my-3">$1</h2>',
    );
    // H3: ### Tiêu đề
    processedContent = processedContent.replace(
      /^### (.+)$/gm,
      '<h3 class="text-xl font-bold my-2">$1</h3>',
    );
    // H4: #### Tiêu đề
    processedContent = processedContent.replace(
      /^#### (.+)$/gm,
      '<h4 class="text-lg font-bold my-2">$1</h4>',
    );
    // H5: ##### Tiêu đề
    processedContent = processedContent.replace(
      /^##### (.+)$/gm,
      '<h5 class="text-base font-bold my-1">$1</h5>',
    );
    // H6: ###### Tiêu đề
    processedContent = processedContent.replace(
      /^###### (.+)$/gm,
      '<h6 class="text-sm font-bold my-1">$1</h6>',
    );

    // Xử lý in đậm (**text** hoặc __text__)
    processedContent = processedContent.replace(
      /\*\*(.*?)\*\*|__(.*?)__/g,
      "<strong>$1$2</strong>",
    );

    // Xử lý in nghiêng (*text* hoặc _text_)
    processedContent = processedContent.replace(
      /\*(.*?)\*|_(.*?)_/g,
      "<em>$1$2</em>",
    );

    // Xử lý gạch ngang (~~ text ~~)
    processedContent = processedContent.replace(/~~(.*?)~~/g, "<del>$1</del>");

    // Xử lý danh sách không thứ tự
    processedContent = processedContent.replace(
      /^\s*[-*+]\s+(.+)$/gm,
      "<li>$1</li>",
    );
    // Nhóm các <li> thành <ul>
    processedContent = processedContent.replace(
      /(<li>.*?<\/li>)\s*\n\s*(?!<li>)/gs,
      '<ul class="list-disc pl-5 my-2">$1</ul>\n',
    );

    // Xử lý danh sách có thứ tự
    processedContent = processedContent.replace(
      /^\s*(\d+)\.\s+(.+)$/gm,
      "<li>$2</li>",
    );
    // Nhóm các <li> có thứ tự thành <ol>
    processedContent = processedContent.replace(
      /(<li>.*?<\/li>)\s*\n\s*(?!<li>)/gs,
      '<ol class="list-decimal pl-5 my-2">$1</ol>\n',
    );

    // Xử lý link [text](url)
    processedContent = processedContent.replace(
      /\[(.*?)\]\((.*?)\)/g,
      '<a href="$2" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>',
    );

    // Xử lý blockquote
    processedContent = processedContent.replace(
      /^>\s+(.+)$/gm,
      '<blockquote class="border-l-4 border-gray-300 pl-4 my-2 italic">$1</blockquote>',
    );

    // Xử lý xuống dòng (2 dấu cách cuối dòng hoặc dòng trống)
    processedContent = processedContent.replace(/  $/gm, "<br />");
    processedContent = processedContent.replace(
      /\n\n/g,
      '<p class="my-1"></p>',
    );

    // Xử lý code inline
    processedContent = processedContent.replace(
      /`([^`]+)`/g,
      '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>',
    );

    // Xử lý code block (cơ bản, không hỗ trợ highlighting)
    processedContent = processedContent.replace(
      /```(?:\w+)?\n([\s\S]+?)\n```/g,
      '<pre class="bg-gray-100 p-3 rounded my-3 overflow-x-auto font-mono text-sm">$1</pre>',
    );

    return processedContent;
  };

  // Kiểm tra nếu đây là bài học cuối cùng trong khóa học
  const isLastLesson = (): boolean => {
    // Logic để kiểm tra nếu đây là bài học cuối cùng
    if (!courseNodes.length || !lesson) return false;

    const lastNode = courseNodes[courseNodes.length - 1];
    return lastNode.id === lesson.id;
  };

  // Hàm giả lập kết nối ví
  const handleConnectWallet = async () => {
    try {
      // Kiểm tra nếu MetaMask được cài đặt
      if (!nftCertificateService.checkIfMetaMaskInstalled()) {
        toast({
          title: "MetaMask chưa được cài đặt",
          description: "Vui lòng cài đặt ví MetaMask để tiếp tục",
          variant: "destructive",
        });

        // Mở trang cài đặt MetaMask
        window.open("https://metamask.io/download.html", "_blank");
        return;
      }

      // Hiển thị thông báo đang kết nối
      toast({
        title: "Đang kết nối ví...",
        description: "Vui lòng xác nhận kết nối trong MetaMask",
        duration: 3000,
      });

      // Kết nối với ví
      const { address } = await nftCertificateService.connectWallet();

      // Cập nhật state
      setWalletAddress(address);
      setWalletConnected(true);
      setShowWalletConnectDialog(false);

      // Hiển thị thông báo kết nối thành công
      toast({
        title: "Đã kết nối ví thành công",
        description: `Địa chỉ ví: ${address.substring(0, 6)}...${address.substring(address.length - 4)}`,
        duration: 3000,
      });
    } catch (error) {
      console.error("Error connecting wallet:", error);

      // Hiển thị thông báo lỗi
      toast({
        title: "Lỗi kết nối ví",
        description: error.message || "Đã xảy ra lỗi khi kết nối ví",
        variant: "destructive",
      });
    }
  };

  // Tạo component Certificate
  const Certificate = () => {
    const formattedDate = new Date().toLocaleDateString("vi-VN");
    const verifyUrl =
      certificate?.public_url ||
      `https://giacngo.org/certificate/${certificate?.certificate_id}`;

    return (
      <div
        id="certificate-container"
        className="certificate-container border-8 border-double border-yellow-600 p-8 text-center bg-[#fffef0] relative mx-auto"
        style={{ width: "850px", height: "600px" }}
      >
        {/* Viền gold */}
        <div className="absolute inset-0 border-2 border-yellow-500 m-1"></div>

        {/* Bánh xe pháp ở 4 góc */}
        <div className="absolute left-4 top-4 h-24">
          <img
            src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/217a27a8-7f1e-4370-9d04-6a8be0bf3b00/public"
            alt="Bánh xe pháp"
            className="h-full opacity-30"
          />
        </div>
        <div className="absolute right-4 top-4 h-24">
          <img
            src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/217a27a8-7f1e-4370-9d04-6a8be0bf3b00/public"
            alt="Bánh xe pháp"
            className="h-full opacity-30"
          />
        </div>
        <div className="absolute left-4 bottom-4 h-24">
          <img
            src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/217a27a8-7f1e-4370-9d04-6a8be0bf3b00/public"
            alt="Bánh xe pháp"
            className="h-full opacity-30"
          />
        </div>
        <div className="absolute right-4 bottom-4 h-24">
          <img
            src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/217a27a8-7f1e-4370-9d04-6a8be0bf3b00/public"
            alt="Bánh xe pháp"
            className="h-full opacity-30"
          />
        </div>

        {/* Bánh xe pháp lớn ở giữa (watermark) */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96">
          <img
            src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/a34fe98e-40ce-43c4-d70b-036ae3ded900/public"
            alt="Bánh xe pháp"
            className="w-full h-full opacity-10"
          />
        </div>

        {/* Logo trung tâm phía trên */}
        <div className="flex justify-center mb-2 relative z-10">
          <img
            src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/19ae6a2c-66a7-4659-1fe9-0ca88aa5a400/public"
            alt="Giác Ngộ"
            className="h-20 w-auto"
          />
        </div>

        {/* Thông tin chứng chỉ */}
        <div className="text-right mb-2 relative z-10">
          <p className="text-sm">
            <span className="font-bold">Ngày cấp:</span>{" "}
            {certificate?.issue_date
              ? new Date(certificate.issue_date).toLocaleDateString("vi-VN")
              : formattedDate}
          </p>
          <p className="text-sm">
            <span className="font-bold">Mã chứng chỉ:</span>{" "}
            {certificate?.certificate_id || "N/A"}
          </p>
        </div>

        {/* Tiêu đề chứng nhận */}
        <div className="my-4 relative z-10">
          <h2 className="text-2xl font-bold text-yellow-800 border-b-2 border-yellow-600 inline-block pb-1">
            CHỨNG NHẬN GIÁC NGỘ
          </h2>
        </div>

        {/* Nội dung chứng nhận */}
        <div className="my-4 relative z-10">
          <p className="text-lg mb-2">Chứng nhận này được trao cho</p>
          <h3 className="text-3xl font-bold text-yellow-900 my-2 font-serif">
            {certificate?.recipient_name || recipientName}
          </h3>
          <p className="text-lg">đã hoàn thành xuất sắc khóa học</p>
          <h4 className="text-2xl font-bold text-yellow-800 mt-2">
            {courseName}
          </h4>
        </div>

        {/* Phần chân chứng chỉ */}
        <div className="flex justify-between items-center mt-6 px-4 relative z-10">
          {/* QR code */}
          <div className="text-left">
            <div className="mb-1">
              <QRCodeSVG value={verifyUrl} size={80} />
            </div>
            <p className="text-xs text-gray-600">Quét để xác thực</p>
          </div>

          {/* Chữ ký */}
          <div className="text-center">
            <img
              src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/a2fbcefe-5221-43b4-fe58-fcc89425fc00/public"
              alt="Chữ ký"
              className="h-16 mx-auto mb-1"
            />
            <p className="text-lg font-bold">Giác Ngộ</p>
            <p className="text-sm">giacngo.org</p>
          </div>
        </div>

        <div>
          <span className="font-medium">Blockchain Network:</span>{" "}
          <span className="text-blue-600 font-medium">Polygon</span>
        </div>
      </div>
    );
  };

  const printCertificate = () => {
    const printContent = document.getElementById("certificate-container");
    if (!printContent) return;

    // Thông báo đang chuẩn bị in
    toast({
      title: "Đang chuẩn bị in chứng chỉ",
      description: "Vui lòng chờ trong giây lát...",
      duration: 3000,
    });

    const originalContents = document.body.innerHTML;
    const printContents = printContent.outerHTML;

    document.body.innerHTML = `
      <style>
        @page { 
          size: landscape; 
          margin: 0; 
        }
        
        html, body { 
          margin: 0; 
          padding: 0; 
          width: 100%; 
          height: 100%; 
          display: flex; 
          justify-content: center; 
          align-items: center; 
          background-color: white; 
        }
        
        #certificate-container {
          width: 850px !important;
          height: 600px !important;
          box-sizing: border-box !important;
          border: 8px double #ca8a04 !important;
          background-color: #fffef0 !important;
          padding: 20px !important;
          text-align: center !important;
          position: relative !important;
          margin: auto !important;
          transform: none !important;
          font-family: 'Helvetica', 'Arial', sans-serif !important;
        }
        
        #certificate-container::before {
          content: '';
          position: absolute;
          inset: 0;
          margin: 4px;
          border: 2px solid #eab308;
          pointer-events: none;
        }
        
        /* Đảm bảo hiển thị các phần tử */
        #certificate-container [class*="absolute"] {
          display: block !important;
          visibility: visible !important;
          position: absolute !important;
        }
        
        /* Đảm bảo opacity phù hợp cho các thành phần */
        #certificate-container img.opacity-30 {
          opacity: 0.3 !important;
        }
        
        #certificate-container img.opacity-5 {
          opacity: 0.05 !important;
        }
        
        /* Giữ nội dung hiển thị trên cùng */
        #certificate-container [class*="z-10"] {
          position: relative !important;
          z-index: 10 !important;
        }
        
        /* Đảm bảo hiển thị đúng của các phần tử flex */
        #certificate-container .flex {
          display: flex !important;
        }
        
        #certificate-container .justify-center {
          justify-content: center !important;
        }
        
        #certificate-container .justify-between {
          justify-content: space-between !important;
        }
        
        #certificate-container .items-center {
          align-items: center !important;
        }
        
        /* Phông chữ và kích thước */
        #certificate-container .text-sm { font-size: 0.875rem !important; }
        #certificate-container .text-lg { font-size: 1.125rem !important; }
        #certificate-container .text-xl { font-size: 1.25rem !important; }
        #certificate-container .text-2xl { font-size: 1.5rem !important; }
        #certificate-container .text-3xl { font-size: 1.875rem !important; }
        #certificate-container .font-bold { font-weight: bold !important; }
        
        @media print {
          html, body {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
          }
          
          #certificate-container {
            page-break-inside: avoid;
            break-inside: avoid;
          }
        }
      </style>
      ${printContents}
    `;

    // Timeout để đảm bảo các hình ảnh được tải trước khi in
    setTimeout(() => {
      window.print();

      // Khôi phục lại nội dung sau khi in
      setTimeout(() => {
        document.body.innerHTML = originalContents;
        window.location.reload();
      }, 100);
    }, 500);
  };

  // Thêm hàm loadNodeProgress để xử lý việc tải tiến độ học tập
  const loadNodeProgress = async (courseId: string) => {
    try {
      // Lấy tiến độ học tập của người dùng
      const progress = await getUserCourseProgress(courseId);
      if (progress && progress.progress) {
        setNodeProgress(progress.progress);
        // Kiểm tra trạng thái hoàn thành của bài học hiện tại
        if (lesson && progress.progress[lesson.id]) {
          setIsCurrentLessonCompleted(true);
        }
      }
    } catch (error) {
      console.error("Error loading node progress:", error);
    }
  };

  // Hàm để đóng nút chat
  const handleCloseChatButton = () => {
    setIsChatButtonVisible(false);
  };

  // Render layout chính
  return (
    <div className={`container mx-auto px-0 sm:px-2 md:px-4 py-2 md:py-4 flex flex-col h-screen ${isMobile ? 'mobile-lesson-view' : ''}`}>
      {/* Thanh điều hướng trên cùng - ẩn trên mobile */}
      {!isMobile && (
        <div className="flex justify-between items-center mb-2 px-4 sm:px-0">
          <Button variant="ghost" onClick={onClose} className="flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("backButton", "Quay lại")}
          </Button>

          <div className="flex items-center gap-3">
            {/* Nút bookmark */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleBookmark}
                    className={bookmarked ? "text-yellow-500" : ""}
                  >
                    {bookmarked ? (
                      <BookmarkCheck className="h-5 w-5" />
                    ) : (
                      <Bookmark className="h-5 w-5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {bookmarked
                    ? t("common:remove_bookmark")
                    : t("common:add_bookmark")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Nút mở chat AI hỗ trợ */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowChatDialog(true)}
                  >
                    <MessageSquare className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("common:ask_ai_help")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      )}

      {/* Thông tin bài học */}
      <div className="bg-white dark:bg-gray-950 rounded-lg shadow-sm flex flex-col flex-grow overflow-hidden">
        {/* Tiêu đề bài học - ẩn trên mobile */}
        {!isMobile && (
          <div className="p-4 border-b">
            <h1 className="text-2xl font-bold">{lesson?.name}</h1>

            {/* Hiển thị tiến trình đọc */}
            <div className="mt-2">
              <div className="flex justify-between text-sm mb-1">
                <span>{t("courses:reading_progress")}</span>
                <span>{Math.min(Math.round(readingProgress), 100)}%</span>
              </div>
              <Progress
                value={Math.min(Math.round(readingProgress), 100)}
                className="h-1"
              />
            </div>
          </div>
        )}

        {/* Chỉ hiển thị thanh tiến trình trên mobile */}
        {isMobile && (
          <div className="progress-bar-mobile">
            <Progress
              value={Math.min(Math.round(readingProgress), 100)}
              className="h-1"
            />
          </div>
        )}

        {/* Nội dung bài học - chỉ phần này có scroll */}
        <div
          ref={contentRef}
          className="p-4 md:p-6 overflow-y-auto flex-grow lesson-content"
        >
          {/* Diagrams ở đầu bài học */}
          {diagramResources
            .filter((d) => d.position === "top")
            .map((diagram) => (
              <div
                key={diagram.id}
                className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center mb-2">
                  <PenTool className="h-5 w-5 mr-2 text-blue-500" />
                  <h3 className="text-lg font-semibold">Sơ đồ minh họa</h3>
                </div>
                <DiagramViewer
                  diagramId={diagram.id || ""}
                  showDetails={diagram.showDetails || false}
                />
              </div>
            ))}

          {/* Nội dung bài học */}
          <div className="lesson-content-text">
            {isHtmlContent(lesson?.content || "") ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: processHtmlContent(lesson?.content || ""),
                }}
              />
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: processMarkdownContent(lesson?.content || ""),
                }}
              />
            )}
          </div>

          {/* Diagrams ở giữa bài học */}
          {diagramResources
            .filter((d) => d.position === "middle")
            .map((diagram) => (
              <div
                key={diagram.id}
                className="my-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center mb-2">
                  <PenTool className="h-5 w-5 mr-2 text-blue-500" />
                  <h3 className="text-lg font-semibold">Sơ đồ minh họa</h3>
                </div>
                <DiagramViewer
                  diagramId={diagram.id || ""}
                  showDetails={diagram.showDetails || false}
                />
              </div>
            ))}

          {/* Diagrams ở cuối bài học */}
          {diagramResources
            .filter((d) => d.position === "bottom" || !d.position)
            .map((diagram) => (
              <div
                key={diagram.id}
                className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center mb-2">
                  <PenTool className="h-5 w-5 mr-2 text-blue-500" />
                  <h3 className="text-lg font-semibold">Sơ đồ minh họa</h3>
                </div>
                <DiagramViewer
                  diagramId={diagram.id || ""}
                  showDetails={diagram.showDetails || false}
                />
              </div>
            ))}

          {/* Nút để tạo certificate nếu đây là bài học cuối cùng */}
          {isLastLesson() && (
            <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="text-xl font-semibold mb-2 text-blue-800 dark:text-blue-300">
                🎉 Chúc mừng! Bạn đã hoàn thành khóa học
              </h3>
              <p className="mb-4 dark:text-gray-300">
                {hasCertificate
                  ? "Bạn đã nhận chứng chỉ hoàn thành khóa học này."
                  : "Bạn đã đến phần cuối của khóa học này. Hãy tạo chứng chỉ để ghi nhận thành tích học tập của mình."}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleGetCertificate}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Award className="mr-2 h-5 w-5" />
                  {hasCertificate
                    ? "Xem chứng chỉ đã nhận"
                    : "Tạo chứng chỉ hoàn thành"}
                </Button>
                <Button variant="outline" onClick={onClose}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Quay lại trang khóa học
                </Button>
              </div>
            </div>
          )}

          {/* Resources của bài học */}
          {lesson?.resources &&
            lesson.resources.filter((r) => r.type !== "diagram").length > 0 && (
              <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-2">
                  Tài liệu tham khảo
                </h3>
                <ul className="space-y-2">
                  {lesson.resources
                    .filter((resource) => resource.type !== "diagram")
                    .map((resource, index) => (
                      <li key={index} className="flex items-start">
                        <span className="mt-0.5 mr-2">
                          {getResourceIcon(resource.type)}
                        </span>
                        <div>
                          <a
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                          >
                            {resource.title}
                          </a>
                          {resource.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {resource.description}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
            )}

          {/* Component đánh giá bài học */}
          <div className="mt-6 mb-12">
            <VoteWidget entityId={lesson?.id || ""} entityType="lesson" />
          </div>
        </div>

        {/* Điều hướng bài học - không trong phần scroll */}
        <div className="p-4 border-t flex justify-between items-center sticky bottom-0 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 shadow-lg z-30 transition-all duration-200">
          <Button
            onClick={onPrev}
            disabled={!hasPrev}
            variant="outline"
            className="flex items-center"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {t("courses:previous_lesson")}
          </Button>

          <Button
            onClick={onNext}
            disabled={!hasNext}
            variant={isCurrentLessonCompleted ? "default" : "outline"}
            className="flex items-center"
          >
            {isCurrentLessonCompleted ? (
              <>
                {t("courses:next_lesson")}
                <ChevronRight className="h-4 w-4 ml-2" />
              </>
            ) : (
              <>
                {t("courses:mark_complete")}
                <Check className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Dialog chat AI */}
      <Dialog open={showChatDialog} onOpenChange={setShowChatDialog}>
        <DialogContent className={cn(
          "flex flex-col transition-all duration-300 ease-in-out p-0",
          isMobile
            ? "max-w-full h-[88vh] rounded-none m-0 animate-in slide-in-from-bottom duration-300"
            : "md:max-w-2xl lg:max-w-4xl h-[85vh] animate-in fade-in-0 zoom-in-95 duration-300"
        )}>
          <DialogHeader className={cn("px-4 py-2 border-b shrink-0")}>
            <DialogTitle>Hỏi AI Trợ Giúp</DialogTitle>
            <DialogDescription>
              Đặt câu hỏi về nội dung này để nhận trợ giúp
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0">
            <ContentChatWidget
              contentType="lesson"
              contentName={lesson?.name || ""}
              contentId={lesson?.id || ""}
              contentDescription={lesson?.description || ""}
              contentData={lesson?.content || ""}
              courseId={courseId}
              suggestedQuestions={suggestedQuestionsForLesson}
              isDialog={true}
              hideHeader={isMobile}
              hideCloseButton={true}
              className="h-full flex flex-col"
              onClose={() => setShowChatDialog(false)}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog tạo certificate */}
      <Dialog
        open={showCertificateDialog}
        onOpenChange={setShowCertificateDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tạo chứng chỉ hoàn thành</DialogTitle>
            <DialogDescription>
              Nhập tên của bạn và chọn loại chứng chỉ
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Tên đầy đủ</Label>
              <Input
                id="name"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Nhập tên đầy đủ của bạn"
              />
            </div>

            <div className="space-y-2">
              <Label>Loại chứng chỉ</Label>
              <div className="grid grid-cols-2 gap-3 pt-1">
                <Button
                  type="button"
                  variant={
                    certificateType === "standard" ? "default" : "outline"
                  }
                  className={`flex flex-col items-center justify-center h-24 ${certificateType === "standard" ? "border-2 border-primary" : ""}`}
                  onClick={() => setCertificateType("standard")}
                >
                  <Award className="h-8 w-8 mb-2" />
                  <div className="text-sm font-medium">Chứng chỉ thường</div>
                  <div className="text-xs text-muted-foreground">Miễn phí</div>
                </Button>

                <Button
                  type="button"
                  variant={certificateType === "nft" ? "default" : "outline"}
                  className={`flex flex-col items-center justify-center h-24 ${certificateType === "nft" ? "border-2 border-primary" : ""}`}
                  onClick={() => setCertificateType("nft")}
                >
                  <div className="flex items-center justify-center h-8 w-8 mb-2">
                    <span className="text-xl">🔗</span>
                  </div>
                  <div className="text-sm font-medium">Chứng chỉ NFT</div>
                  <div className="text-xs text-muted-foreground">
                    Trên Polygon
                  </div>
                </Button>
              </div>

              {certificateType === "nft" && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mt-3 text-sm">
                  <p className="flex items-start">
                    <span className="text-yellow-600 mr-2">⚠️</span>
                    <span>
                      Chứng chỉ NFT sẽ được mint trên Polygon.{" "}
                      {!walletConnected
                        ? "Bạn cần kết nối ví và trả phí gas."
                        : `Bạn đã kết nối ví ${walletAddress.substring(0, 6)}...${walletAddress.substring(walletAddress.length - 4)} và sẽ trả phí gas.`}
                    </span>
                  </p>
                  {!walletConnected && (
                    <div className="mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleConnectWallet}
                        className="flex items-center text-xs"
                      >
                        <span className="text-sm mr-1">🦊</span>
                        Kết nối ví MetaMask
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              onClick={handleGenerateCertificate}
              disabled={isGeneratingCertificate}
            >
              {isGeneratingCertificate ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Đang tạo...
                </>
              ) : certificateType === "nft" ? (
                "Tạo chứng chỉ NFT"
              ) : (
                "Tạo chứng chỉ"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog hiển thị certificate đã tạo */}
      <Dialog
        open={showCertificateResultDialog}
        onOpenChange={setShowCertificateResultDialog}
      >
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Chứng chỉ của bạn</DialogTitle>
            <DialogDescription>
              Chúc mừng bạn đã hoàn thành khóa học!
            </DialogDescription>
            {certificate?.nft_token_id && (
              <Badge
                variant="outline"
                className="bg-blue-50 text-blue-700 border-blue-200 mt-2"
              >
                NFT Certificate
              </Badge>
            )}
          </DialogHeader>

          <div className="certificate-view-container overflow-auto max-h-[70vh] md:max-h-[80vh] p-2 relative">
            <div className="certificate-wrapper flex justify-center items-center bg-gray-50 rounded-lg p-4">
              <div
                className="certificate-scale-container"
                style={{
                  transform: "scale(0.75)",
                  transformOrigin: "center center",
                  margin: "0 auto",
                }}
              >
                <Certificate />
              </div>
            </div>
            <div className="py-2 text-center text-sm text-gray-500">
              <p>Cuộn hoặc thu phóng để xem đầy đủ chứng chỉ</p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={printCertificate}>
              <Printer className="mr-2 h-4 w-4" />
              In chứng chỉ
            </Button>
            <Button onClick={handleDownloadCertificate}>
              <Download className="mr-2 h-4 w-4" />
              Tải chứng chỉ
            </Button>
            {nftCertificateCreated && (
              <Button
                variant="outline"
                className="border-green-500 text-green-600 hover:bg-green-50"
                onClick={() =>
                  window.open(
                    `https://polygonscan.com/tx/${nftTxHash}`,
                    "_blank",
                  )
                }
              >
                <Globe className="mr-2 h-4 w-4" />
                Xem NFT trên Polygon Scan
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog kết nối ví */}
      <Dialog
        open={showWalletConnectDialog}
        onOpenChange={setShowWalletConnectDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kết nối ví để tạo NFT</DialogTitle>
            <DialogDescription>
              Bạn cần kết nối ví để tạo chứng chỉ NFT trên Polygon
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
              <p className="mb-2 font-medium text-blue-800">
                Lưu ý quan trọng:
              </p>
              <ul className="space-y-1 list-disc pl-5 text-blue-700">
                <li>Đảm bảo bạn có MATIC để thanh toán phí gas</li>
                <li>Kiểm tra mạng đang kết nối là Polygon</li>
                <li>Chứng chỉ NFT sẽ được gửi đến ví của bạn</li>
              </ul>
            </div>

            <div className="flex justify-center py-4">
              <Button
                variant="outline"
                className="w-full h-16 border-2"
                onClick={handleConnectWallet}
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg"
                  alt="MetaMask"
                  className="h-8 mr-3"
                />
                <span>Kết nối với MetaMask</span>
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowWalletConnectDialog(false)}
            >
              Hủy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chat floating button for quick questions */}
      {lesson && (
        <LessonChatButton
          courseId={courseId}
          lessonId={lesson.id}
          lessonName={lesson.name}
          lessonContent={lesson.content}
          lessonDescription={lesson.description}
          suggestedQuestions={suggestedQuestionsForLesson}
          hidden={showCertificateDialog || showCertificateResultDialog || showChatDialog || isChatOpen || showWalletConnectDialog || showQuiz || !isChatButtonVisible}
          onClose={handleCloseChatButton}
          onChatButtonClick={() => setShowChatDialog(true)}
        />
      )}
    </div>
  );
};

export default LessonContentView;
